if (!customElements.get('fh-product-reviews')) {
  customElements.define('fh-product-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.reviews?.length) this.showReviews();
        if (!this.fallbackFailed && this.reviews.length < this.reviewLimit()) this.deferFallback();
        return;
      }
      const source = this.querySelector('[data-review-source]');
      if (!source) return;
      this.initialized = true;
      this.track = this.querySelector('[data-track]');
      this.dialog = this.querySelector('dialog');
      this.reviews = this.readReviews(source.content);
      if (this.reviews.length) {
        this.renderSummary(source.content);
        this.showReviews();
        if (this.reviews.length < this.reviewLimit()) this.deferFallback();
      } else this.prepareFallback();
    }

    showReviews() {
      if (!this.rendered) this.track.replaceChildren();
      const renderedCount = this.track.children.length;
      this.reviews.slice(renderedCount).forEach((review, index) => this.renderCard(review, renderedCount + index));
      this.track.toggleAttribute('data-single', this.reviews.length === 1);
      this.querySelector('[data-navigation]').hidden = this.reviews.length < 2;
      this.hidden = false;
      this.removeAttribute('aria-busy');
      delete this.dataset.loading;
      this.rendered = true;
      this.connectControls();
      this.resizeObserver?.observe(this.track);
      this.updatePosition();
    }

    prepareFallback() {
      if (!this.dataset.poolUrl || !this.dataset.poolModule) return;
      this.dataset.fallback = 'true';
      this.dataset.loading = 'true';
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-title]').textContent = this.dataset.fallbackTitle;
      const status = this.element('p', 'fh-pr__loading', this.dataset.loadingLabel);
      status.setAttribute('role', 'status');
      this.track.replaceChildren(status);
      this.querySelector('[data-navigation]').hidden = false;
      this.querySelectorAll('[data-previous], [data-next]').forEach(button => { button.disabled = true; });
      this.hidden = false;
      this.deferFallback();
    }

    deferFallback() {
      if (this.loadingTask || !this.dataset.poolUrl) return;
      this.intersectionObserver?.disconnect();
      if (!('IntersectionObserver' in window)) return this.loadFallback();
      this.intersectionObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.intersectionObserver.disconnect();
        this.loadFallback();
      }, { rootMargin: '400px 0px' });
      this.intersectionObserver.observe(this);
    }

    loadFallback() {
      const remaining = this.reviewLimit() - this.reviews.length;
      if (this.loadingTask || remaining <= 0) return;
      this.loadingTask = import(this.dataset.poolModule)
        .then(module => module.loadReviewSelection(this.dataset.poolUrl, this.dataset.productId, remaining,
          review => this.hasReviewDetails(review)))
        .then(reviews => {
          if (!reviews.length) throw new Error('No eligible fallback reviews');
          this.reviews = [...this.reviews, ...reviews].slice(0, this.reviewLimit());
          if (this.isConnected) this.showReviews();
        })
        .catch(() => {
          this.fallbackFailed = true;
          this.hidden = this.reviews.length === 0;
          this.removeAttribute('aria-busy');
        });
    }

    reviewLimit() {
      return Math.min(10, Math.max(1, Number(this.dataset.limit) || 10));
    }

    readReviews(source) {
      return [...source.querySelectorAll('.jdgm-rev')].map(node => ({
        name: this.text(node, '.jdgm-rev__author') || this.dataset.customerLabel,
        body: this.reviewText(node),
        title: this.text(node, '.jdgm-rev__title'),
        rating: Number(node.querySelector('.jdgm-rev__rating')?.dataset.score),
        verified: node.dataset.verifiedBuyer === 'true',
        productTitle: this.dataset.productTitle,
        productUrl: node.dataset.productUrl
      })).filter(review => this.hasReviewDetails(review)
        && (!review.productUrl || review.productUrl === this.dataset.productUrl)).slice(0, this.reviewLimit());
    }

    hasReviewDetails(review) {
      if (!Number.isInteger(review.rating) || review.rating < 4 || review.rating > 5) return false;
      const body = (review.body || '').normalize('NFKC').toLowerCase();
      const words = body.match(/\p{L}[\p{L}\p{M}'’]*/gu) || [];
      if (!words.length || (words.length > 2 && new Set(words).size === 1)) return false;
      const normalized = text => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
      if (normalized(body) === normalized(review.productTitle || '')) return false;
      if (/[?؟@]|https?:\/\/|www\./u.test(body) || /\b(?:kindly|please)\b/u.test(body)) return false;
      const concern = /\b(?:broken|damaged|defective|faulty|poor|bad|terrible|disappointed|disappointing|refund|complaint|delayed|late)\b|\bnot (?:good|working|satisfied|recommended|original)\b|\b(?:doesn['’]?t|does not|won['’]?t) work\b/u;
      if (concern.test(body)) return false;
      const negatedPraise = /\b(?:not|never|don['’]?t|doesn['’]?t|wouldn['’]?t)\s+(?:(?:very|really|fully)\s+)?(?:good|great|excellent|happy|satisfied|recommend(?:ed)?|like|love)\b/u;
      if (negatedPraise.test(body)) return false;
      // General or brief praise is valid; do not require a product-feature keyword.
      const praise = /\b(?:good|great|excellent|amazing|best|perfect(?:ly)?|wonderful|reliable|satisfied|happy|smooth|easy|fast|quick|reasonable|competitive|affordable|genuine|original|authentic|superb|efficient|useful|powerful|love(?:d|ly)?|like(?:d)?|recommend(?:ed)?|thank(?:s)?)\b|بہترین|اچھ[ایے]|زبردست/u;
      return praise.test(body);
    }

    text(node, selector) {
      return node.querySelector(selector)?.textContent.trim() || '';
    }

    reviewText(node) {
      const body = node.querySelector('.jdgm-rev__body')?.cloneNode(true);
      if (!body) return '';
      body.querySelectorAll('br').forEach(line => line.replaceWith('\n'));
      body.querySelectorAll('p, div').forEach(paragraph => paragraph.append('\n\n'));
      return body.textContent.trim().replace(/\n{3,}/g, '\n\n');
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    renderSummary(source) {
      const summary = source.querySelector('.jdgm-rev-widg');
      const average = Number(summary?.dataset.averageRating);
      const count = Number(summary?.dataset.numberOfReviews);
      if (average && count) {
        const locale = document.documentElement.lang || 'en';
        const score = average.toLocaleString(locale, { maximumFractionDigits: 2 });
        const total = count.toLocaleString(locale);
        const text = `${score}/5 · ${this.dataset.reviewCount.replace('[count]', total)}`;
        this.querySelector('[data-summary]').textContent = this.dataset.productSummary.replace('[summary]', text);
      }
      const fullWidget = document.getElementById('judgeme_product_reviews');
      this.querySelector('[data-all-reviews]').hidden = !fullWidget;
    }

    renderHeader(review) {
      const header = this.element('div', 'fh-pr__card-header');
      const identity = this.element('div', 'fh-pr__identity');
      const name = this.element('p', 'fh-pr__name', review.name);
      name.title = review.name;
      identity.append(name);
      const stars = this.element('span', 'fh-pr__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.dataset.scoreLabel.replace('[rating]', review.rating));
      header.append(identity, stars);
      return header;
    }

    renderCard(review, index) {
      const card = this.element('article', 'fh-pr__card');
      card.toggleAttribute('data-reviewed-product', Boolean(review.productTitle));
      const button = this.element('button', 'fh-pr__read', this.dataset.readLabel);
      button.type = 'button';
      button.dataset.reviewIndex = index;
      card.append(this.renderHeader(review), this.element('p', 'fh-pr__quote', review.body));
      if (review.productTitle) card.append(this.productLabel(review));
      card.append(button);
      this.track.append(card);
    }

    productLabel(review) {
      const label = this.element('p', 'fh-pr__product', `${this.dataset.productLabel}: ${review.productTitle}`);
      label.title = label.textContent;
      return label;
    }

    connectControls() {
      if (this.controlsConnected) return;
      this.controlsConnected = true;
      this.addEventListener('click', event => {
        const read = event.target.closest('[data-review-index]');
        if (read) this.openReview(Number(read.dataset.reviewIndex), read, event.detail > 0);
        if (event.target.closest('[data-close]')) {
          this.pointerDialog = event.detail > 0;
          this.reviewOpener?.toggleAttribute('data-pointer-focus', this.pointerDialog);
          this.dialog.close();
        }
        if (event.target.closest('[data-previous]')) this.move(-1);
        if (event.target.closest('[data-next]')) this.move(1);
      });
      this.addEventListener('keydown', () => {
        this.pointerDialog = false;
        this.querySelectorAll('[data-pointer-focus]').forEach(node => node.removeAttribute('data-pointer-focus'));
      });
      this.addEventListener('focusout', event => event.target.removeAttribute('data-pointer-focus'));
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.reviewOpener = null;
      });
      this.track.addEventListener('scroll', () => this.updatePosition(), { passive: true });
      this.track.addEventListener('keydown', event => this.handleKey(event));
      this.resizeObserver = new ResizeObserver(() => this.updatePosition());
      this.resizeObserver.observe(this.track);
    }

    handleKey(event) {
      if (event.target !== this.track) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.track.scrollTo({ left: 0, behavior: 'instant' });
      else if (event.key === 'End') this.track.scrollTo({ left: this.isRtl() ? -this.track.scrollWidth : this.track.scrollWidth, behavior: 'instant' });
      else this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.isRtl() ? -1 : 1));
    }

    isRtl() {
      return getComputedStyle(this.track).direction === 'rtl';
    }

    move(direction) {
      const distance = this.track.firstElementChild.getBoundingClientRect().width + 12;
      this.track.scrollBy({ left: distance * direction * (this.isRtl() ? -1 : 1), behavior: 'instant' });
    }

    updatePosition() {
      const distance = this.track.firstElementChild.getBoundingClientRect().width + 12;
      const left = Math.abs(this.track.scrollLeft);
      const max = this.track.scrollWidth - this.track.clientWidth;
      const index = left >= max - 2 ? this.reviews.length : Math.min(this.reviews.length, Math.floor(left / distance) + 1);
      this.querySelector('[data-position]').textContent = `${index} / ${this.reviews.length}`;
      this.querySelector('[data-previous]').disabled = left < 2;
      this.querySelector('[data-next]').disabled = left >= max - 2;
    }

    focusControl(control, pointer) {
      control.toggleAttribute('data-pointer-focus', pointer);
      control.focus({ preventScroll: true });
    }

    openReview(index, opener, pointer) {
      const review = this.reviews[index];
      const body = this.querySelector('[data-full-review]');
      body.replaceChildren(this.renderHeader(review));
      if (review.productTitle) body.append(this.productLabel(review));
      if (review.title) body.append(this.element('p', 'fh-pr__dialog-title', review.title));
      body.append(this.element('p', 'fh-pr__quote', review.body));
      this.reviewOpener = opener;
      this.pointerDialog = pointer;
      // Safari does not focus clicked buttons; give the native dialog an exact return target.
      this.focusControl(opener, pointer);
      this.dialog.showModal();
      this.focusControl(this.dialog.querySelector('[data-close]'), pointer);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
    }
  });
}
